// decorators.ts
import { A } from "@adornis/base/env-info";
import { handleClientRequest } from "./client/requestHandler";
import "./entities/adornisEntity";
import {
  allOperationDefinitions,
  BQLReflect,
  metaDataStore,
  metaState,
  mutations,
  queries,
  staticFields,
  subscriptions
} from "./metaDataStore";
import { upgradeMiddleware } from "./server/middlewares/upgradeArguments";
import { handleServerRequest } from "./server/requestHandler";
function unwrapType(type) {
  return () => unwrapTypeRec(type());
}
function unwrapTypeRec(type) {
  return Array.isArray(type) ? unwrapTypeRec(type[0]) : type;
}
function getArrayDepth(type) {
  return () => getArrayDepthRec(type());
}
function getArrayDepthRec(type, depth = 0) {
  return Array.isArray(type) ? getArrayDepthRec(type[0], depth + 1) : depth;
}
function Entity({ description = "" } = {}) {
  return (target) => {
    if (metaState.sealed)
      throw new Error(
        "Entity decorator " + target._class + " ran after schema creation and will not be availiable. Check import order"
      );
    let fields = BQLReflect.getMetadata(target);
    if (!fields) {
      console.warn(`Entity ${target._class} has no field metadata.`);
      fields = [...staticFields];
    }
    const proto = Object.getPrototypeOf(target);
    const protoNode = metaDataStore.find((node) => node.type() === proto);
    if (!protoNode)
      throw new Error(
        `Superclass ${proto._class} of ${target._class} is not (yet?) present in dependency tree. Check your initialization order!`
      );
    protoNode.children.push({
      type: () => target,
      parent: protoNode,
      fields,
      options: { description },
      children: []
    });
  };
}
function Field(type, options = {}) {
  return (target, propertyName) => {
    let currentFields = BQLReflect.getMetadata(target.constructor);
    if (!currentFields)
      currentFields = [...staticFields];
    const unwrappedType = unwrapType(type);
    currentFields.push({
      name: propertyName,
      type: unwrappedType,
      arrayDepth: getArrayDepth(type),
      options,
      params: [],
      middlewares: []
    });
    BQLReflect.setMetadata(currentFields, target.constructor);
  };
}
function applyMiddleware(middleware) {
  return (target, propertyName) => {
    const isStatic = target.constructor === Function;
    let currentFields = isStatic ? allOperationDefinitions() : BQLReflect.getMetadata(target.constructor);
    if (!currentFields)
      throw new Error(
        "Cannot applyMiddleware to non defined field. Please use @applyMiddleware after (=== above) @Field or @Query"
      );
    const fields = currentFields.filter((field) => field.name === propertyName);
    if (!fields.length)
      throw new Error(
        "Cannot applyMiddleware to non defined field. Please use @applyMiddleware after (=== above) @Field or @Query"
      );
    fields.forEach((field) => field.middlewares.push(middleware));
  };
}
function Arg(name, type) {
  return (target, propertyName, parameterIndex) => {
    const argData = BQLReflect.getMetadata(target, propertyName) || [];
    const unwrappedType = unwrapType(type);
    argData.push({ name, type: unwrappedType, arrayDepth: getArrayDepth(type), parameterIndex });
    BQLReflect.setMetadata(argData, target, propertyName);
  };
}
var Query = (type, options) => operationHelper(type, queries, "query", options);
var Mutation = (type, options) => operationHelper(type, mutations, "mutation", options);
var Subscription = (type, options) => operationHelper(type, subscriptions, "subscription", options);
function operationHelper(type, operations, opType, options) {
  return (target, propertyName, descriptor) => {
    if (!target._class)
      throw new Error("Queries only allowed as static functions ( " + propertyName + " )");
    if (operations.find((op2) => op2.name === propertyName))
      throw new Error(`Trying to define duplicate operation ${propertyName}`);
    const unwrappedType = unwrapType(type);
    const arrayDepth = getArrayDepth(type);
    const params = (BQLReflect.getMetadata(target, propertyName) || []).reverse();
    const op = {
      name: propertyName,
      type: unwrappedType,
      arrayDepth,
      options: { ...options, resolve: descriptor.value },
      params,
      middlewares: A.isServer ? [upgradeMiddleware] : [],
      originalThis: target
    };
    operations.push(op);
    const argNames = params.map((arg) => arg.name);
    if (A.isClient) {
      descriptor.value = handleClientRequest(argNames, opType, op);
    } else {
      descriptor.value = handleServerRequest(argNames, opType, op);
    }
    return descriptor;
  };
}
export {
  Arg,
  Entity,
  Field,
  Mutation,
  Query,
  Subscription,
  applyMiddleware,
  getArrayDepth,
  operationHelper,
  unwrapType
};
